import { useContext, useMemo } from 'react';
import { ApolloError, useQuery, ApolloQueryResult } from '@apollo/client';
import { useAuth } from '@providers/AuthProvider';
import authenticatedMemberInvoiceProposals from '@graphql/queries/authenticatedMemberInvoiceProposals.gql';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';
import { omitBy, isNil } from 'lodash';
import { billTypes } from '@data/bills';
export interface IMemberInvoiceProposal {
	invoice?: {
		total: number;
		amountPaid: number;
		amountRemaining: number;
		created: Date;
		paymentMethod: {
			card: {
				brand: string;
				last4: string;
			};
		};
		kind: keyof typeof billTypes;
	};
	id: string;
	creationDate: Date;
	dueDate: Date;
	status: string;
	total: number;
	lineItems: IMemberInvoiceProposalLineItem[];
}

export interface IMemberInvoiceProposalLineItem {
	description: string;
	price: number;
	quantity: number;
	instructions: string;
}

interface IAuthenticatedMemberInvoiceProposal {
	authenticatedMember: {
		invoiceProposals: IMemberInvoiceProposal[];
	};
}

type UseMemberInvoiceProposal = (params?: { invoiceId?: string | null; status?: string[] }) => {
	loading: boolean;
	data?: IMemberInvoiceProposal[];
	error?: ApolloError;
	refetchMemberInvoiceProposals: () => Promise<
		ApolloQueryResult<IAuthenticatedMemberInvoiceProposal>
	>;
};

// TODO use status filter when available in BE
const UseMemberInvoiceProposal: UseMemberInvoiceProposal = (
	params = { invoiceId: undefined, status: undefined }
) => {
	const { invoiceId, status } = params;
	const { loggedIn } = useAuth();
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loading, error, data, refetch } = useQuery<IAuthenticatedMemberInvoiceProposal>(
		authenticatedMemberInvoiceProposals,
		{
			variables: omitBy({ id: invoiceId }, isNil),
			client: graphQLv2Client,
			skip: !loggedIn,
			errorPolicy: 'all',
		}
	);

	const filteredData: IMemberInvoiceProposal[] | undefined = useMemo(() => {
		const proposals = data?.authenticatedMember.invoiceProposals ?? [];
		return status?.length
			? proposals.filter((invoice) => status.includes(invoice.status))
			: proposals;
	}, [data, status]);

	return {
		loading,
		data: loading ? undefined : filteredData ?? [],
		error,
		refetchMemberInvoiceProposals: refetch,
	};
};

export default UseMemberInvoiceProposal;
